/**
 * 환경설정 &gt; 자동전달 (migration 162, 2026-09-05 사용자 지시).
 *
 * 사용여부 스위치 + 자동전달 메일주소(추가·전체삭제) + 예외 자동전달 규칙(추가·전체삭제·수정).
 *
 * ★★★ **저장바가 없다** — 스팸·자동분류 탭과 같다(정본 P2-1 「반영 시점의 단위는 탭」).
 * ★★★ **바깥으로 못 보내는 사정을 «미리» 말한다.** 회사 밖 주소는 「외부 메일 계정」의
 *    보내기(SMTP)로 나가는데, 계정이 없는 사람은 그 사실을 **켜고 한참 뒤에야** 알게 된다 —
 *    그래서 서버가 `canSendExternal` 을 함께 주고 화면이 그 자리에서 적는다(정본 P3).
 * ★★ 꺼져 있어도 목록은 보인다 — 주소를 적어 두고 켜는 순서다. 다만 「지금은 안 보낸다」를 말한다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addMailForwardAddress, clearMailForwardAddresses, clearMailForwardRules, createMailForwardRule,
  deleteMailForwardAddress, deleteMailForwardRule, getMailForward, updateMailForwardRule,
  type MailForwardAddress, type MailForwardRule, type MailForwardRuleInput,
} from '../../api/mail';
import { getMySettings, patchMySettings } from '../../api/users';
import { Edit3, Plus, Trash2 } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Switch } from '../../components/shared/Switch';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailAddressRuleModal } from './MailAddressRuleModal';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailForwardRuleModal } from './MailForwardRuleModal';

type Ask =
  | { kind: 'addr'; row: MailForwardAddress }
  | { kind: 'rule'; row: MailForwardRule }
  | { kind: 'clear-addr' }
  | { kind: 'clear-rule' };

/** 서버가 준 코드 → 사람 말. */
function messageFor(err: unknown, fallback: MailI18nKey): string {
  const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
  switch (code) {
    case 'INVALID_ADDRESS': return tMail('mail.fwd.err_addr');
    case 'INVALID_DOMAIN': return tMail('mail.fwd.err_domain');
    case 'SELF_ADDRESS': return tMail('mail.fwd.err_self');
    case 'ADDRESS_EXISTS': return tMail('mail.fwd.err_exists');
    case 'RULE_EXISTS': return tMail('mail.fwd.err_rule_exists');
    case 'NO_FORWARD_ADDRESS': return tMail('mail.fwd.err_targets');
    default: return tMail(fallback);
  }
}

export function MailSettingsForward() {
  const [enabled, setEnabled] = useState(false);
  const [addresses, setAddresses] = useState<MailForwardAddress[]>([]);
  const [rules, setRules] = useState<MailForwardRule[]>([]);
  const [canSendExternal, setCanSendExternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [ruleFor, setRuleFor] = useState<{ rule: MailForwardRule | null } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const state = await getMailForward();
    setAddresses(state.addresses);
    setRules(state.rules);
    setCanSendExternal(state.canSendExternal);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getMySettings(), getMailForward()])
      .then(([s, state]) => {
        if (cancelled) return;
        setEnabled(s.mailForwardEnabled === true);
        setAddresses(state.addresses);
        setRules(state.rules);
        setCanSendExternal(state.canSendExternal);
      })
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.fwd.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /** 스위치 — 고른 순간 저장하고, 실패하면 **되돌린다**. */
  const toggle = useCallback(async (next: boolean) => {
    setEnabled(next);
    setNote(null);
    try {
      await patchMySettings({ mailForwardEnabled: next });
      setNote({ ok: true, text: tMail('mail.fwd.saved') });
    } catch (err) {
      setEnabled(!next);
      const status = (err as { response?: { status?: number } }).response?.status;
      setNote({ ok: false, text: tMail(status === 400 ? 'mail.fwd.legacy' : 'mail.fwd.save_failed') });
    }
  }, []);

  const run = useCallback(async (
    fn: () => Promise<void>, failKey: MailI18nKey, inModal = false,
  ): Promise<boolean> => {
    setBusy(true);
    setModalErr(null);
    setNote(null);
    try {
      await fn();
      await reload();
      setNote({ ok: true, text: tMail('mail.fwd.saved') });
      return true;
    } catch (err) {
      const msg = messageFor(err, failKey);
      if (inModal) setModalErr(msg); else setNote({ ok: false, text: msg });
      return false;
    } finally {
      setBusy(false);
    }
  }, [reload]);

  const addrColumns: ReadonlyArray<TableColumn<MailForwardAddress>> = useMemo(() => [
    { key: 'address', header: tMail('mail.fwd.col.address'), rowHeader: true, render: (r) => r.address },
    {
      key: 'manage',
      header: tMail('mail.fwd.col.manage'),
      width: '96px',
      render: (r) => (
        <Button
          variant="quiet-danger" size="sm" disabled={busy}
          iconLeft={<Trash2 size={13} aria-hidden />}
          aria-label={tMail('mail.fwd.delete_aria').replace('{n}', r.address)}
          onClick={() => setAsk({ kind: 'addr', row: r })}
        >
          {tMail('mail.fwd.delete')}
        </Button>
      ),
    },
  ], [busy]);

  const ruleColumns: ReadonlyArray<TableColumn<MailForwardRule>> = useMemo(() => [
    {
      key: 'match',
      header: tMail('mail.fwd.col.match'),
      rowHeader: true,
      render: (r) => (
        <span className="filtrule__name">
          <span className="fwdrule__kind">
            {tMail(r.matchKind === 'DOMAIN' ? 'mail.fwd.kind.domain' : 'mail.fwd.kind.address')}
          </span>
          <span className="filtrule__nm">{r.matchValue}</span>
        </span>
      ),
    },
    {
      key: 'targets',
      header: tMail('mail.fwd.col.targets'),
      render: (r) => (
        <span className="filtrule__cond">
          {r.addresses.map((a) => <span key={a}>{a}</span>)}
        </span>
      ),
    },
    {
      key: 'manage',
      header: tMail('mail.fwd.col.manage'),
      width: '170px',
      render: (r) => (
        <span className="mbox__acts">
          <Button
            variant="secondary" size="sm" disabled={busy}
            iconLeft={<Edit3 size={13} aria-hidden />}
            onClick={() => { setModalErr(null); setRuleFor({ rule: r }); }}
          >
            {tMail('mail.fwd.edit')}
          </Button>
          <Button
            variant="quiet-danger" size="sm" disabled={busy}
            onClick={() => setAsk({ kind: 'rule', row: r })}
          >
            {tMail('mail.fwd.delete')}
          </Button>
        </span>
      ),
    },
  ], [busy]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby="mfwd-h">
        <h2 className="wc-set__sech" id="mfwd-h">{tMail('mail.fwd.sec.use')}</h2>
        <div className="wc-set__rows">
          <div className="wc-set__row">
            <span className="wc-set__label">{tMail('mail.fwd.use_row')}</span>
            <div className="wc-set__ctl">
              <Switch
                checked={enabled}
                onCheckedChange={(next) => { void toggle(next); }}
                label={tMail('mail.fwd.use_label')}
              />
              {!enabled && <p className="wc-set__hint">{tMail('mail.fwd.off_hint')}</p>}
              {/* ★★★ 못 보내는 사정을 **미리** 말한다 — 켜고 한참 뒤에 알면 늦다. */}
              <p className={canSendExternal ? 'wc-set__hint' : 'wc-set__bad'}>
                {tMail(canSendExternal ? 'mail.fwd.external_ok' : 'mail.fwd.external_none')}
              </p>
              <p className={note?.ok === false ? 'wc-set__bad' : 'wc-set__ok'} role="status">
                {note?.text ?? ''}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="wc-set__sec" aria-labelledby="mfwda-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mfwda-h">{tMail('mail.fwd.sec.addr')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setAddrOpen(true); }}
          >
            {tMail('mail.fwd.add')}
          </Button>
          <Button
            variant="quiet-danger" size="sm"
            disabled={addresses.length === 0 || busy}
            onClick={() => setAsk({ kind: 'clear-addr' })}
          >
            {tMail('mail.fwd.clear')}
          </Button>
        </div>
        <Table
          caption={tMail('mail.fwd.sec.addr')}
          columns={addrColumns}
          rows={addresses}
          rowKey={(r) => r.id}
          empty={<span>{tMail('mail.fwd.addr_none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
      </section>

      <section className="wc-set__sec" aria-labelledby="mfwdr-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mfwdr-h">{tMail('mail.fwd.sec.rule')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setRuleFor({ rule: null }); }}
          >
            {tMail('mail.fwd.add')}
          </Button>
          <Button
            variant="quiet-danger" size="sm"
            disabled={rules.length === 0 || busy}
            onClick={() => setAsk({ kind: 'clear-rule' })}
          >
            {tMail('mail.fwd.clear')}
          </Button>
        </div>
        <Table
          caption={tMail('mail.fwd.sec.rule')}
          columns={ruleColumns}
          rows={rules}
          rowKey={(r) => r.id}
          empty={<span>{tMail('mail.fwd.rule_none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
        <p className="wc-set__hint">{tMail('mail.fwd.rule_hint')}</p>
      </section>

      {addrOpen && (
        <MailAddressRuleModal
          kind="ALLOW"
          title={tMail('mail.fwd.addr_title')}
          busy={busy}
          error={modalErr}
          onClose={() => { setAddrOpen(false); setModalErr(null); }}
          onSubmit={(address) => {
            void (async () => {
              const ok = await run(async () => { await addMailForwardAddress(address); },
                'mail.fwd.err_failed', true);
              if (ok) setAddrOpen(false);
            })();
          }}
        />
      )}

      {ruleFor && (
        <MailForwardRuleModal
          target={ruleFor.rule}
          busy={busy}
          error={modalErr}
          onClose={() => { setRuleFor(null); setModalErr(null); }}
          onSubmit={(input: MailForwardRuleInput) => {
            void (async () => {
              const editing = ruleFor.rule;
              const ok = await run(async () => {
                if (editing) await updateMailForwardRule(editing.id, input);
                else await createMailForwardRule(input);
              }, 'mail.fwd.err_failed', true);
              if (ok) setRuleFor(null);
            })();
          }}
        />
      )}

      <MailConfirmDialog
        open={ask !== null}
        danger
        busy={busy}
        title={
          ask?.kind === 'addr' ? tMail('mail.fwd.delete_aria').replace('{n}', ask.row.address)
            : ask?.kind === 'rule' ? tMail('mail.fwd.del_rule_title').replace('{n}', ask.row.matchValue)
              : ask?.kind === 'clear-addr' ? tMail('mail.fwd.clear_addr_title')
                : ask?.kind === 'clear-rule' ? tMail('mail.fwd.clear_rule_title')
                  : ''
        }
        description={tMail('mail.fwd.clear_desc')}
        confirmLabel={tMail('mail.fwd.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void (async () => {
            const ok = await run(async () => {
              if (cur.kind === 'addr') await deleteMailForwardAddress(cur.row.id);
              else if (cur.kind === 'rule') await deleteMailForwardRule(cur.row.id);
              else if (cur.kind === 'clear-addr') await clearMailForwardAddresses();
              else await clearMailForwardRules();
            }, 'mail.fwd.save_failed');
            if (ok) setAsk(null);
          })();
        }}
      />
    </div>
  );
}
